<template>
  <div class="app-questions list">
    <PageHeader :title="`Sondages ${application.app.label}`"/>
    <SurveysSubmenu />
    <PageContent>
      <MVPTable :gridData="gridData" :options="gridOptions" />
    </PageContent>
  </div>
</template>

<script>
import { http } from '@/services/api.service'
import { mapGetters } from 'vuex'
import { confirmMediaDuplication, isDuplicatedByDomain } from '@/utils/mediaDuplicationConfirm'
import MVPTable from '@/components/agGrid/MVPTable.vue'
import AG_GRID_LOCALE_FR from '@/assets/ag-grid.locale.fr.json'
import PageHeader from '@/components/PageHeader.vue'
import PageContent from '@/components/PageContent.vue'
import ButtonCellRenderer from '@/components/agGrid/ButtonCellRenderer.vue'
import SurveysSubmenu from './SurveysSubmenu.vue'
import ListRowDetail from './ListRowDetail.vue'
import OneGroup from './Modals/OneGroup.vue'
import MVPMediaDetailRenderer from './MVPMediaDetailRenderer.vue'

export default {
  name: 'app-surveys-media-mvp',
  components: {
    PageHeader,
    SurveysSubmenu,
    PageContent,
    ButtonCellRenderer, //eslint-disable-line
    ListRowDetail, //eslint-disable-line
    MVPMediaDetailRenderer, //eslint-disable-line
    MVPTable
  },
  computed: {
    ...mapGetters(['roles', 'user', 'domain', 'application']),
    hasEditionRights () {
      return this.roles.some(role => ['admin', 'director', 'community-manager'].includes(role))
    },
    columnDefs () {
      return [
        { cellRenderer: 'agGroupCellRenderer', width: 10, suppressMenu: true },
        {
          headerName: 'Titre',
          field: 'group_title',
          sortable: true,
          filter: true,
          valueGetter: params => {
            const title = params.data.group_title
            if (title && title.trim() !== '') return title
            const surveys = params.data.surveys
            if (Array.isArray(surveys) && surveys.length > 0 && surveys[0].question) {
              return surveys[0].question
            }
            return '(Sans titre)'
          }
        },
        // Date columns hidden for media-mvp
        // {
        //   headerName: 'Date début',
        //   field: 'start_at',
        //   cellRenderer: 'DateCellRenderer',
        //   sortable: true,
        //   filter: true
        // },
        // {
        //   headerName: 'Date fin',
        //   field: 'end_at',
        //   cellRenderer: 'DateCellRenderer',
        //   sortable: true,
        //   filter: true
        // },
        // Status column hidden for media-mvp
        // {
        //   headerName: 'Statut',
        //   field: 'is_enabled',
        //   valueFormatter: params => params.value ? 'Activé' : 'Désactivé',
        //   sortable: true,
        //   filter: true
        // },
        {
          headerName: 'Action',
          field: 'button',
          cellRenderer: 'ButtonCellRenderer',
          cellRendererParams: {
            onClick: this.duplicate,
            buttonText: 'Dupliquer'
          },
          width: 200,
          hide: !this.hasEditionRights,
          sortable: false
        },
        {
          headerName: 'Dupliqué ?',
          field: 'duplicated_by',
          valueGetter: params => {
            const duplicatedBy = params.data.duplicated_by
            const domainUuid = this.domain.uuid
            return duplicatedBy && duplicatedBy.includes(domainUuid) ? 'Oui' : 'Non'
          },
          sortable: true,
          filter: true
        }
      ]
    }
  },
  data () {
    return {
      surveyGroups: [],
      gridData: null,
      duplicating: false,
      gridOptions: {
        rowModelType: 'clientSide',
        localeText: AG_GRID_LOCALE_FR,
        masterDetail: true,
        frameworkComponents: {
          MVPMediaDetailRenderer: MVPMediaDetailRenderer
        },
        detailCellRenderer: 'MVPMediaDetailRenderer',
        detailRowAutoHeight: true,
        detailCellRendererParams: {
        },
        columnDefs: [],
        defaultColDef: {
          resizable: true,
          floatingFilter: true
        }
      }
    }
  },
  watch: {
    roles () {
      this.updateColumnDefs()
    }
  },
  mounted () {
    this.getSurveyGroups()
    this.updateColumnDefs()
  },
  methods: {
    filterByApplication (entries) {
      const slug = this.application?.app?.slug
      if (!Array.isArray(entries)) {
        return []
      }
      if (!slug || !['maviepro', 'tract'].includes(slug)) {
        return entries
      }
      return entries.filter(entry => {
        const visibility = entry?.media_mvp_visibility || 'both'
        return visibility === 'both' || visibility === slug
      })
    },
    getSurveyGroups () {
      this.loading = true
      http
        .get('/surveys/surveys-mvp')
        .then(res => {
          this.loading = false
          const filteredGroups = this.filterByApplication(res.data)
          this.surveyGroups = filteredGroups
          this.gridData = filteredGroups
        })
        .catch(() => {
          this.$buefy.notification.open({
            duration: 3000,
            message:
              'Nos serveurs rencontrent des difficultés à récuperer la liste des sondages',
            position: 'is-bottom-left',
            type: 'is-warning',
            hasIcon: true,
            icon: 'server'
          })
        })
    },
    duplicate (surveyGroup) {
      if (this.duplicating) return // Prevent multiple clicks

      confirmMediaDuplication(this, {
        alreadyDuplicated: isDuplicatedByDomain(surveyGroup.duplicated_by, this.domain.uuid),
        contentLabel: 'ce sondage',
        title: 'Duplication du sondage',
        message:
          'Voulez-vous vraiment dupliquer ce sondage ?<br><br>En confirmant, il sera automatiquement dupliqué sur votre domaine et vous serez redirigé vers la liste des sondages inactifs.',
        onConfirm: (payload) => {
          this.duplicating = true

          const loadingNotification = this.$buefy.notification.open({
            duration: 0,
            message: 'Duplication en cours...',
            position: 'is-bottom-right',
            type: 'is-info',
            hasIcon: true,
            icon: 'loading',
            indefinite: true
          })

          http.post(`/surveys/${surveyGroup.uuid}/duplicate`, payload).then(
            res => {
              const newGroup = res.data && res.data.group ? res.data.group : null

              loadingNotification.close()

              this.$buefy.notification.open({
                duration: 4000,
                message: 'Sondage dupliqué avec succès ! Redirection vers les sondages inactifs...',
                position: 'is-bottom-right',
                type: 'is-success',
                hasIcon: true,
                icon: 'check'
              })

              try {
                const redirectPromise = this.$router.push({
                  path: '/app/multisurveys',
                  query: { status: 'inactive', refreshToken: Date.now().toString() }
                })

                if (redirectPromise && redirectPromise.then) {
                  redirectPromise.then(() => {
                    if (newGroup) {
                      setTimeout(() => {
                        this.$buefy.dialog.confirm({
                          title: 'Configuration du sondage dupliqué',
                          message: `Voulez-vous configurer les dates pour le sondage "${(newGroup.group_title && newGroup.group_title.trim()) ? newGroup.group_title : (newGroup.surveys && newGroup.surveys[0] && newGroup.surveys[0].question ? newGroup.surveys[0].question : '(Sans titre)')}" ?`,
                          confirmText: 'Configurer',
                          cancelText: 'Plus tard',
                          onConfirm: () => {
                            this.$buefy.modal.open({
                              parent: this,
                              component: OneGroup,
                              hasModalCard: true,
                              customClass: 'content-editor-modal-host',
                              width: 960,
                              canCancel: ['escape', 'outside'],
                              destroyOnHide: false,
                              ariaRole: 'dialog',
                              ariaModal: true,
                              props: {
                                editGroup: newGroup
                              },
                              events: {
                                updated: (updatedGroupData) => {
                                  let isEnabled = false
                                  if (updatedGroupData?.group?.is_enabled !== undefined) {
                                    isEnabled = updatedGroupData.group.is_enabled
                                  } else if (updatedGroupData?.is_enabled !== undefined) {
                                    isEnabled = updatedGroupData.is_enabled
                                  }

                                  const targetStatus = isEnabled ? 'active' : 'inactive'
                                  const currentStatus = this.$route.query.status || 'active'
                                  const refreshToken = Date.now().toString()

                                  if (targetStatus !== currentStatus) {
                                    this.$router.push({
                                      path: '/app/multisurveys',
                                      query: { status: targetStatus, refreshToken }
                                    }).catch(() => {})
                                  } else {
                                    this.$router.replace({
                                      path: this.$route.path,
                                      query: { ...this.$route.query, refreshToken }
                                    }).catch(() => {})
                                  }

                                  this.$buefy.notification.open({
                                    duration: 4000,
                                    message: 'Le sondage a été mis à jour avec succès !',
                                    position: 'is-bottom-right',
                                    type: 'is-success',
                                    hasIcon: true
                                  })
                                }
                              }
                            })
                          }
                        })
                      }, 500)
                    }
                  }).catch(() => {
                    window.location.href = '/app/multisurveys?status=inactive'
                  })
                }
              } catch (routerError) {
                window.location.href = '/app/multisurveys?status=inactive'
              }

              this.duplicating = false
            },
            () => {
              // Close loading notification
              loadingNotification.close()
              this.duplicating = false

              this.$buefy.notification.open({
                duration: 3000,
                message: 'Erreur lors de la duplication du sondage',
                position: 'is-bottom-right',
                type: 'is-danger',
                hasIcon: true,
                icon: 'alert'
              })
            }
          )
        }
      })
    },
    updateColumnDefs () {
      this.gridOptions.columnDefs = this.columnDefs
      if (this.gridOptions.api) {
        this.gridOptions.api.setColumnDefs(this.columnDefs)
      }
    }
  }
}
</script>
